/**
 * Shared live presence at `events/{eventId}/presence/{uid}`.
 *
 * Only the owning uid may write their own doc. Other clients listen so the
 * host list can drop a guest from "N online" without touching participants.
 */
import { collection, doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { buildPresenceOffline, buildPresenceOnline } from '../utils/presence';
import { describeError } from '../utils/errorLog';

export function presenceRef(eventId, uid) {
  return doc(db, 'events', eventId, 'presence', uid);
}

export function presenceCollection(eventId) {
  return collection(db, 'events', eventId, 'presence');
}

function isPermissionDenied(error) {
  return error?.code === 'permission-denied';
}

function logPresenceFailure(action, error) {
  const detail = describeError(error);
  if (isPermissionDenied(error)) {
    console.error(
      `Presence ${action} denied (permission-denied). `
      + 'The live Firestore rules may not include events/{eventId}/presence yet. '
      + detail,
    );
    return;
  }
  console.error(`Failed to mark presence ${action}:`, detail);
}

export async function markPresenceOnline({ eventId, uid } = {}) {
  if (!eventId || !uid) return null;
  const payload = buildPresenceOnline(uid);
  try {
    await setDoc(presenceRef(eventId, uid), payload, { merge: true });
    return payload;
  } catch (error) {
    logPresenceFailure('online write', error);
    return null;
  }
}

export async function markPresenceOffline({ eventId, uid } = {}) {
  if (!eventId || !uid) return null;
  const payload = buildPresenceOffline(uid);
  try {
    await setDoc(presenceRef(eventId, uid), payload, { merge: true });
    return payload;
  } catch (error) {
    logPresenceFailure('offline write', error);
    return null;
  }
}

export function mapPresenceSnapshot(snapshot) {
  const next = {};
  const docs = snapshot?.docs || [];
  docs.forEach((entry) => {
    next[entry.id] = { uid: entry.id, ...entry.data() };
  });
  return next;
}

export function watchPresence(eventId, onChange) {
  if (!eventId || typeof onChange !== 'function') return () => {};
  return onSnapshot(presenceCollection(eventId), (snapshot) => {
    onChange(mapPresenceSnapshot(snapshot));
  }, (error) => {
    if (isPermissionDenied(error)) {
      console.error(
        'Presence listener denied (permission-denied). '
        + 'The live Firestore rules may not allow reading events/{eventId}/presence. '
        + describeError(error),
      );
      return;
    }
    console.error('Presence listener failed:', describeError(error));
  });
}
